<template>
  <MainLayout>
    <!-- Orders Sub Navigation (hidden in fullscreen) -->
    <OrdersSubNavbar v-if="!isFullscreen" />

    <div
      :class="isFullscreen ? 'fixed inset-0 z-50 bg-white dark:bg-gray-900 flex flex-col' : 'kitchen-view space-y-4 sm:space-y-6 px-3 sm:px-6'"
    >
      <!-- Fullscreen Header -->
      <div
        v-if="isFullscreen"
        class="flex-shrink-0 px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700"
      >
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-4 flex-1">
            <h2 class="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
              {{ 'Cocina' }}
            </h2>

            <!-- Filters in fullscreen - compact version -->
            <KitchenFilters
              v-model:selected-status="selectedStatus"
              v-model:selected-order-type="selectedOrderType"
              :status-tabs="statusTabs"
              :order-type-tabs="orderTypeTabs"
            />
          </div>

          <div class="flex items-center gap-3">
            <!-- Button Visibility Toggle -->
            <div class="flex items-center gap-2">
              <label class="text-xs font-medium text-gray-600 dark:text-gray-300">
                Botones
              </label>
              <button
                type="button"
                :class="[
                  'relative inline-flex h-5 w-9 items-center rounded-full transition-colors',
                  showButtons ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-600'
                ]"
                @click="showButtons = !showButtons"
              >
                <span
                  :class="[
                    'inline-block h-3 w-3 transform rounded-full bg-white transition-transform',
                    showButtons ? 'translate-x-5' : 'translate-x-1'
                  ]"
                />
              </button>
            </div>

            <div class="text-sm text-gray-500 dark:text-gray-400">
              {{ t('app.views.kitchen.last_updated', { time: new Date().toLocaleTimeString() }) }}
            </div>

            <button
              type="button"
              class="rounded-md bg-white dark:bg-gray-800 p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-100 dark:focus:ring-offset-gray-900 transition-colors"
              @click="exitFullscreen"
            >
              <span class="sr-only">Salir de pantalla completa</span>
              <XMarkIcon
                class="h-6 w-6"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
      </div>

      <!-- Normal Header -->
      <PageHeader
        v-else
        :title="t('app.views.kitchen.title')"
      >
        <template #actions>
          <BaseButton
            class="hidden sm:flex"
            @click="enterFullscreen"
          >
            <ArrowsPointingOutIcon class="h-4 w-4" />
            Pantalla Completa
          </BaseButton>
        </template>
      </PageHeader>

      <!-- Filters (hidden in fullscreen since they're in header) -->
      <KitchenFilters
        v-if="!isFullscreen"
        v-model:selected-status="selectedStatus"
        v-model:selected-order-type="selectedOrderType"
        :status-tabs="statusTabs"
        :order-type-tabs="orderTypeTabs"
      />

      <!-- Scrollable Content Area -->
      <div :class="isFullscreen ? 'flex-1 overflow-auto p-4 sm:p-6' : ''">
        <!-- Loading State -->
        <LoadingSpinner
          v-if="loading"
          size="lg"
          color="primary"
          :show-text="false"
        />

        <!-- Grouped View -->
        <KitchenGroupedView
          v-else-if="selectedStatus === 'grouped'"
          :grouped-items="groupedItems"
        />

        <!-- Orders View (All, Pending, Preparing) -->
        <div v-else>
          <!-- Empty State -->
          <EmptyState
            v-if="filteredOrders.length === 0"
            icon="shopping"
            :title="t('app.views.kitchen.no_active')"
            :description="t('app.views.kitchen.no_active_description')"
            variant="card"
            size="md"
          />

          <!-- Orders Grid -->
          <div
            v-else
            class="columns-1 md:columns-2 lg:columns-4 column-gap-2 sm:column-gap-3"
          >
            <KitchenOrderCard
              v-for="order in filteredOrders"
              :key="order.id"
              :order="order"
              :show-buttons="isFullscreen ? showButtons : true"
              class="break-inside-avoid mb-3 sm:mb-4"
              @start-preparing="handleMarkItemPreparing"
              @mark-ready="handleMarkItemReady"
              @start-preparing-all="handleStartPreparingAll"
              @mark-all-ready="handleMarkAllReady"
            />
          </div>
        </div>
      </div>
    </div>
  </MainLayout>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLocalStorage } from '@vueuse/core';
import { XMarkIcon, ArrowsPointingOutIcon } from '@heroicons/vue/24/outline';
import BaseButton from '@/components/common/BaseButton.vue';
import MainLayout from '@/components/layout/MainLayout.vue';
import PageHeader from '@/components/layout/PageHeader.vue';
import OrdersSubNavbar from '@/components/orders/OrdersSubNavbar.vue';
import type { Order, OrderItem } from '@/services/orderService';
import { useKitchenOrders } from '@/composables/useKitchenOrders';
import { useKitchenFilters, type KitchenStatusFilter, type OrderTypeFilter } from '@/composables/useKitchenFilters';
import { useKitchenItems } from '@/composables/useKitchenItems';
import EmptyState from '@/components/base/EmptyState.vue';
import KitchenFilters from '@/components/kitchen/KitchenFilters.vue';
import KitchenOrderCard from '@/components/kitchen/KitchenOrderCard.vue';
import KitchenGroupedView from '@/components/kitchen/KitchenGroupedView.vue';
import LoadingSpinner from '@/components/base/LoadingSpinner.vue';

const { t } = useI18n();

// State
const selectedStatus = ref<KitchenStatusFilter>('pending');
const selectedOrderType = ref<OrderTypeFilter | undefined>(undefined);
const isFullscreen = ref(false);
const showButtons = ref(false);

// Fullscreen functions
const enterFullscreen = () => {
  isFullscreen.value = true;
};

const exitFullscreen = () => {
  isFullscreen.value = false;
};

// Handle ESC key to exit fullscreen
watch(isFullscreen, (fullscreen) => {
  if (fullscreen) {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        exitFullscreen();
      }
    };
    document.addEventListener('keydown', handleEsc);

    // Cleanup on unmount or when exiting fullscreen
    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }
});

// Composables
const { loading, activeOrders, fetchActiveOrders } = useKitchenOrders(isFullscreen);
const { filteredOrders, groupedItems, statusTabs, orderTypeTabs } = useKitchenFilters(
  activeOrders,
  selectedStatus,
  selectedOrderType,
  t
);
const { markItemPreparing, markItemReady, startPreparingAllItems, markAllItemsReady } = useKitchenItems(activeOrders);

// Event handlers - Delegate to composable methods
const handleMarkItemPreparing = (order: Order, item: OrderItem) => {
  markItemPreparing(order, item);
};

const handleMarkItemReady = (order: Order, item: OrderItem) => {
  markItemReady(order, item);
};

const handleStartPreparingAll = (order: Order) => {
  startPreparingAllItems(order);
};

const handleMarkAllReady = (order: Order) => {
  markAllItemsReady(order);
};
</script>

<style scoped>
.kitchen-view {
  max-width: 1800px;
  margin: 0 auto;
}
</style>
